import { useEffect, useState } from 'react'
import { MdClose, MdLock, MdLockOpen, MdVisibility, MdVisibilityOff } from 'react-icons/md'

interface Props {
    isOpen: boolean
    /** 'unlock' pede a senha existente; 'set' define ou remove a proteção. */
    mode: 'unlock' | 'set'
    targetName: string
    hint?: string | null
    hasPassword?: boolean
    loading?: boolean
    onClose: () => void
    onConfirm: (password: string | null, hint?: string) => void
}

/**
 * Senha de arquivo ou pasta.
 *
 * No modo 'set', deixar o campo vazio remove a proteção — e o aviso deixa isso
 * explícito, porque remover senha por engano é silencioso demais para ficar
 * implícito num campo em branco.
 */
export default function StoragePasswordModal({
    isOpen,
    mode,
    targetName,
    hint,
    hasPassword,
    loading,
    onClose,
    onConfirm,
}: Props) {
    const [password, setPassword] = useState('')
    const [confirmation, setConfirmation] = useState('')
    const [newHint, setNewHint] = useState('')
    const [visible, setVisible] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (isOpen) {
            setPassword('')
            setConfirmation('')
            setNewHint(hint ?? '')
            setVisible(false)
            setError('')
        }
    }, [isOpen, hint])

    if (!isOpen) return null

    const isRemoving = mode === 'set' && hasPassword && password.length === 0

    const handleConfirm = () => {
        setError('')

        if (mode === 'unlock') {
            if (!password) {
                setError('Informe a senha')
                return
            }
            onConfirm(password)
            return
        }

        if (isRemoving) {
            onConfirm(null)
            return
        }

        if (password.length < 4) {
            setError('A senha deve ter pelo menos 4 caracteres')
            return
        }
        if (password !== confirmation) {
            setError('As senhas não conferem')
            return
        }

        onConfirm(password, newHint || undefined)
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-md">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        {mode === 'unlock' ? (
                            <MdLockOpen className="text-xl text-warning" />
                        ) : (
                            <MdLock className="text-xl text-primary" />
                        )}
                        {mode === 'unlock' ? 'Desbloquear' : 'Proteger com senha'}
                    </h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={onClose}
                        disabled={loading}
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <p className="text-sm text-base-content/70 mb-5 truncate">{targetName}</p>

                {mode === 'unlock' && hint && (
                    <div className="alert alert-info mb-4 py-2">
                        <span className="text-sm">Dica: {hint}</span>
                    </div>
                )}

                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label pb-1.5" htmlFor="storage-password">
                            <span className="label-text font-medium">
                                {mode === 'unlock' ? 'Senha' : 'Nova senha'}
                            </span>
                        </label>
                        <div className="join w-full">
                            <input
                                id="storage-password"
                                type={visible ? 'text' : 'password'}
                                className="input input-bordered join-item w-full"
                                value={password}
                                autoFocus
                                placeholder={
                                    mode === 'set' && hasPassword
                                        ? 'Deixe vazio para remover a proteção'
                                        : 'Digite a senha'
                                }
                                onChange={event => setPassword(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') handleConfirm()
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-ghost join-item border border-base-300"
                                onClick={() => setVisible(v => !v)}
                                tabIndex={-1}
                            >
                                {visible ? <MdVisibilityOff /> : <MdVisibility />}
                            </button>
                        </div>
                    </div>

                    {mode === 'set' && !isRemoving && (
                        <>
                            <div className="form-control w-full">
                                <label className="label pb-1.5" htmlFor="storage-password-confirm">
                                    <span className="label-text font-medium">
                                        Confirmar senha
                                    </span>
                                </label>
                                <input
                                    id="storage-password-confirm"
                                    type={visible ? 'text' : 'password'}
                                    className="input input-bordered w-full"
                                    value={confirmation}
                                    onChange={event => setConfirmation(event.target.value)}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1.5" htmlFor="storage-password-hint">
                                    <span className="label-text font-medium">
                                        Dica da senha
                                    </span>
                                    <span className="label-text-alt text-base-content/50">
                                        Opcional
                                    </span>
                                </label>
                                <input
                                    id="storage-password-hint"
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={newHint}
                                    placeholder="Exibida a quem tentar abrir"
                                    onChange={event => setNewHint(event.target.value)}
                                />
                            </div>
                        </>
                    )}

                    {isRemoving && (
                        <div className="alert alert-warning py-2">
                            <span className="text-sm">
                                Com o campo vazio, a proteção por senha será removida.
                            </span>
                        </div>
                    )}

                    {error && <p className="text-sm text-error">{error}</p>}
                </div>

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className={`btn ${isRemoving ? 'btn-warning' : 'btn-primary'}`}
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading && <span className="loading loading-spinner loading-sm" />}
                        {mode === 'unlock'
                            ? 'Desbloquear'
                            : isRemoving
                              ? 'Remover proteção'
                              : 'Salvar senha'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
